/* My family — the people in your household who have no phone of their own.
 *
 * Children, mostly, and an elderly parent. They are checked in with you on
 * Sunday and counted in the church, without needing an account.
 */
import { useState } from 'react'
import { Loader2, Pencil, Plus, Trash2, UsersRound } from 'lucide-react'
import {
  useHousehold,
  useRemoveHouseholdMember,
  useSaveHouseholdMember,
} from '@/api/queries'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field, SelectField } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import { relationLabel } from '@/features/attendance/parts'
import type { HouseholdMember } from '@/types/api'

export function FamilyPage() {
  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">My family</h1>
        <p className="text-base text-ink-2">
          Add the people who come to church with you but have no phone of
          their own. You can check them in along with yourself.
        </p>
      </header>
      <Household />
    </div>
  )
}

/** The list with add, edit and remove. With `headId`, somebody else's
 *  household as an administrator sees it: adding only. */
export function Household({
  churchId,
  headId,
  headName,
}: {
  churchId?: string | null
  headId?: string
  headName?: string
}) {
  const household = useHousehold(churchId, headId)
  const remove = useRemoveHouseholdMember()
  const [editing, setEditing] = useState<HouseholdMember | 'new' | null>(null)
  const [removing, setRemoving] = useState<HouseholdMember | null>(null)
  const own = !headId

  if (household.isLoading) {
    return (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  }
  if (household.isError || !household.data) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(household.error as Error)?.message || 'This did not load.'}
      </Card>
    )
  }

  const { items, relations } = household.data

  return (
    <div className="space-y-s3">
      {items.length ? (
        <Card className="divide-y divide-line-2">
          {items.map((m) => (
            <div key={m.id} className="flex items-center gap-s3 px-s4 py-s3">
              <Avatar name={m.name} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-semibold">{m.name}</p>
                <p className="text-sm text-ink-2">
                  {relationLabel(m.relation)}
                  {m.birthday
                    ? ` · born ${new Date(m.birthday + 'T00:00').toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}`
                    : ''}
                </p>
              </div>
              {own ? (
                <>
                  <button
                    onClick={() => setEditing(m)}
                    aria-label={`Edit ${m.name}`}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-pill text-ink-2 hover:bg-hover-2"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                  <button
                    onClick={() => setRemoving(m)}
                    aria-label={`Remove ${m.name}`}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-pill text-ink-2 hover:bg-hover-2 hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </>
              ) : null}
            </div>
          ))}
        </Card>
      ) : (
        <Card className="p-s6 text-center">
          <UsersRound className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s2 text-base text-ink-2">
            {own ? 'Nobody added yet.' : `No family added for ${headName ?? 'this person'} yet.`}
          </p>
        </Card>
      )}

      <Button variant="primary" onClick={() => setEditing('new')}>
        <Plus className="h-4 w-4" aria-hidden />
        Add a family member
      </Button>

      {editing ? (
        <MemberDialog
          churchId={churchId}
          headId={headId}
          member={editing === 'new' ? null : editing}
          relations={relations}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Confirm
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.first_name ?? ''}?`}
        body="They will no longer be checked in with you or counted at church."
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() =>
          removing && remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })
        }
      />
    </div>
  )
}

function MemberDialog({
  churchId,
  headId,
  member,
  relations,
  onClose,
}: {
  churchId?: string | null
  headId?: string
  member: HouseholdMember | null
  relations: string[]
  onClose: () => void
}) {
  const save = useSaveHouseholdMember(churchId, headId)
  const [first, setFirst] = useState(member?.first_name ?? '')
  const [last, setLast] = useState(member?.last_name ?? '')
  const [relation, setRelation] = useState(member?.relation ?? relations[0] ?? '')
  const [birthday, setBirthday] = useState(member?.birthday?.slice(0, 10) ?? '')
  const [gender, setGender] = useState<string>(member?.gender ?? '')
  const [error, setError] = useState<string | null>(null)

  function submit() {
    if (!first.trim() || save.isPending) return
    setError(null)
    save.mutate(
      {
        id: member?.id,
        input: {
          first_name: first.trim(),
          last_name: last.trim() || null,
          relation: relation || null,
          birthday: birthday || null,
          gender: (gender || null) as 'male' | 'female' | null,
        },
      },
      {
        onSuccess: onClose,
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={member ? `Edit ${member.first_name}` : 'Add a family member'}
      width="460px"
      footer={
        <Button variant="primary" size="lg" full onClick={submit} disabled={!first.trim()} loading={save.isPending}>
          {member ? 'Save' : 'Add'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <div className="grid gap-s3 sm:grid-cols-2">
          <Field
            label="First name"
            value={first}
            onChange={(e) => setFirst(e.target.value.slice(0, 80))}
            autoFocus
            required
          />
          <Field label="Last name" value={last} onChange={(e) => setLast(e.target.value.slice(0, 80))} />
        </div>
        <SelectField
          label="Relation"
          value={relation}
          onChange={(e) => setRelation(e.target.value)}
          options={relations.map((r) => ({ value: r, label: relationLabel(r) }))}
        />
        <div className="grid gap-s3 sm:grid-cols-2">
          <Field
            label="Birthday (optional)"
            type="date"
            value={birthday}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setBirthday(e.target.value)}
          />
          <SelectField
            label="Gender (optional)"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            options={[
              { value: '', label: 'Not said' },
              { value: 'female', label: 'Female' },
              { value: 'male', label: 'Male' },
            ]}
          />
        </div>
        {error ? (
          <p role="alert" className="rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
